import React, { useEffect, useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ApiResponseDeactivateDnsZone } from '../../../types/api/ApiResponseDomainName';

interface DeactivateDnsZoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  domainId: number;
  apex: string;
  onDeactivated: () => void | Promise<void>; // called on success (parent refetches the domain)
}

function DeactivateDnsZoneModal({ isOpen, onClose, domainId, apex, onDeactivated }: DeactivateDnsZoneModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset the submit error whenever the modal (re)opens (mirrors RecordSetDeleteModal's reset).
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleDeactivate = async () => {
    if (domainId <= 0 || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // DB-only unbind; no request body. Idempotent server-side.
      const response = await apiFetch(`/datacenters/domain-names/${domainId}/zone`, { method: 'DELETE' });

      let body: ApiResponseDeactivateDnsZone | null;
      try {
        body = (await response.json()) as ApiResponseDeactivateDnsZone;
      } catch {
        body = null;
      }

      if (!response.ok || !body || body.status !== 'success') {
        throw new Error(body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim());
      }

      addNotification({
        title: 'DNS zone deactivated',
        srTitle: 'Success alert',
        description: `The DNS zone activation for ${apex} was removed.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      await onDeactivated();
      onClose();
    } catch (err) {
      // Keep the modal open and surface the backend reason inline.
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={handleClose} aria-label="Remove DNS zone activation">
      <ModalHeader title="Remove DNS zone activation" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Could not remove DNS zone activation" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content>
          Remove the DNS zone activation for <strong>{apex}</strong>? This unbinds the domain from its nameserver
          endpoint and stops managing its DNS here. Any zone still on the endpoint is left untouched and can be
          re-activated later.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => void handleDeactivate()}
          isLoading={isSubmitting}
          isDisabled={isSubmitting || domainId <= 0}
        >
          Remove activation
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default DeactivateDnsZoneModal;
